<template>
  <div>
    <div v-if="loading" class="flex items-center justify-center">
      <Spinner></Spinner>
    </div>
    <div v-else-if="bookingData.bookings.length === 0" class="text-center text-gray-500">
      No booking data available
    </div>
    <div v-else>
      <div class="mb-6">
        <label for="teacher-select" class="block text-sm font-medium text-gray-700 mb-2">
          Filter by Teacher:
        </label>
        <select
            id="teacher-select"
            v-model="selectedTeacherId"
            @change="fetchBookings"
            class="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm mb-4"
        >
          <option value="">All Teachers</option>
          <option v-for="teacher in teachers" :key="teacher.id" :value="teacher.id">
            {{ teacher.name }}
          </option>
        </select>
      </div>

      <h3 class="text-xl font-medium mb-4">Bookings & Platform Fees by Month</h3>
      <div class="bg-white rounded-lg shadow my-4 p-6">
        <div class="mb-4">
          <Bar :data="chartData" :options="chartOptions"/>
        </div>
        <div class="text-sm text-gray-500 text-center">
          Total Bookings: {{ totalBookings }} | Total Platform Fees: £{{ totalPlatformFees }}
        </div>
      </div>

      <h3 class="text-xl font-medium mb-4 mt-8">Student Engagement by Month</h3>
      <div class="bg-white rounded-lg shadow my-4 p-6">
        <div class="mb-4">
          <Bar :data="studentChartData" :options="studentChartOptions"/>
        </div>
        <div class="text-sm text-gray-500 text-center">
          Total Unique Students: {{ totalUniqueStudents }} | Average Bookings per Student: {{
            averageBookingsPerStudent
          }}
        </div>
      </div>

      <h3 class="text-xl font-medium mb-4 mt-8">Student Loyalty by Month</h3>
      <div class="bg-white rounded-lg shadow my-4 p-6">
        <div class="mb-4">
          <Bar :data="loyaltyChartData" :options="loyaltyChartOptions"/>
        </div>
        <div class="text-sm text-gray-500 text-center">
          Total First-Time Students: {{ totalFirstTimeStudents }} | Total Return Students: {{ totalReturnStudents }}
        </div>
      </div>

      <h3 class="text-xl font-medium mb-4 mt-8">Bookings per Teacher by Month</h3>
      <div class="bg-white rounded-lg shadow my-4 p-6">
        <div class="mb-4">
          <Line :data="teacherChartData" :options="teacherChartOptions"/>
        </div>
        <div class="text-sm text-gray-500 text-center">
          Total Buckets: {{ totalTeachers }} | Total Teacher Bookings: {{ totalTeacherBookings }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Bar, Line } from 'vue-chartjs';
import Chart from 'chart.js/auto';

import {
  Title,
  Tooltip,
  Legend,
  BarElement,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement
} from 'chart.js';
import Spinner from './Spinner.vue';

Chart.register(Title, Tooltip, Legend, BarElement, CategoryScale, LinearScale, LineElement, PointElement);

const loading = ref(true);
const bookingData = ref({bookings: [], total: 0});
const teacherData = ref({bookings: [], total: 0});
const loyaltyData = ref({studentLoyalty: [], total: 0});
const teachers = ref([]);
const selectedTeacherId = ref('');

onMounted(() => {
  fetchTeachers();
  fetchBookings();
  fetchTeacherData();
  fetchLoyaltyData();
});

const fetchTeachers = async () => {
  try {
    const response = await fetch('/api/stats/stats-teachers-list?json=1');
    const data = await response.json();
    teachers.value = data;
  } catch (error) {
    console.error('Error fetching teachers:', error);
  }
};

const fetchBookings = async () => {
  try {
    const url = selectedTeacherId.value 
      ? `/api/stats/stats?json=1&teacherId=${selectedTeacherId.value}`
      : '/api/stats/stats?json=1';
    const response = await fetch(url);
    const data = await response.json();
    bookingData.value = data;
    loading.value = false;
  } catch (error) {
    console.error('Error fetching bookings:', error);
    loading.value = false;
  }
};

const fetchTeacherData = async () => {
  try {
    const response = await fetch('/api/stats/stats-teachers?json=1');
    const data = await response.json();
    teacherData.value = data;
  } catch (error) {
    console.error('Error fetching teacher data:', error);
  }
};

const fetchLoyaltyData = async () => {
  try {
    const response = await fetch('/api/stats/stats-students-loyalty?json=1');
    const data = await response.json();
    loyaltyData.value = data;
  } catch (error) {
    console.error('Error fetching loyalty data:', error);
  }
};

const chartData = computed(() => {
  if (!bookingData.value.bookings.length) {
    return {labels: [], datasets: []};
  }

  const labels = bookingData.value.bookings.map(booking => booking.month);
  const bookingCounts = bookingData.value.bookings.map(booking => booking.count);
  const platformFees = bookingData.value.bookings.map(booking => booking.platformFeeSum);

  return {
    labels,
    datasets: [
      {
        label: 'Platform Fee (£)',
        data: platformFees,
        backgroundColor: '#f59e0b',
        borderColor: '#d97706',
        borderWidth: 1,
        type: 'bar',
        yAxisID: 'y'
      },
      {
        label: 'Bookings',
        data: bookingCounts,
        backgroundColor: '#14b8a6',
        borderColor: '#0f766e',
        borderWidth: 3,
        fill: false,
        type: 'line',

        yAxisID: 'y1',
        pointRadius: 4,
        pointHoverRadius: 6,
        order: 1
      }
    ]
  };
});

const chartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    title: {
      display: false
    }
  },
  scales: {
    x: {
      stacked: false
    },
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      beginAtZero: true,
      title: {
        display: true,
        text: 'Platform Fee (£)'
      }
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      beginAtZero: true,
      title: {
        display: true,
        text: 'Bookings Count'
      },
      ticks: {
        stepSize: 1
      },
      grid: {
        drawOnChartArea: false,
      }
    }
  }
}));

const totalBookings = computed(() => {
  return bookingData.value.total;
});

const totalPlatformFees = computed(() => {
  return bookingData.value.bookings.reduce((sum, booking) => sum + (booking.platformFeeSum || 0), 0).toFixed(2);
});

const studentChartData = computed(() => {
  if (!bookingData.value.bookings.length) {
    return {labels: [], datasets: []};
  }

  const labels = bookingData.value.bookings.map(booking => booking.month);
  const uniqueStudents = bookingData.value.bookings.map(booking => booking.uniqueStudents || 0);
  const avgBookingsPerStudent = bookingData.value.bookings.map(booking => {
    const students = booking.uniqueStudents || 0;
    const bookings = booking.count || 0;
    return students > 0 ? (bookings / students).toFixed(2) : 0;
  });

  return {
    labels,
    datasets: [
      {
        label: 'Unique Students',
        data: uniqueStudents,
        backgroundColor: '#3b82f6',
        borderColor: '#1d4ed8',
        borderWidth: 1,
        type: 'bar',
        yAxisID: 'y'
      },
      {
        label: 'Avg Bookings per Student',
        data: avgBookingsPerStudent,
        backgroundColor: '#f97316',
        borderColor: '#ea580c',
        borderWidth: 3,
        fill: false,
        type: 'line',
        yAxisID: 'y1',
        pointRadius: 4,
        pointHoverRadius: 6,
        order: 1
      }
    ]
  };
});

const studentChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    title: {
      display: false
    }
  },
  scales: {
    x: {
      stacked: false
    },
    y: {
      type: 'linear',
      display: true,
      position: 'left',
      beginAtZero: true,
      title: {
        display: true,
        text: 'Unique Students'
      }
    },
    y1: {
      type: 'linear',
      display: true,
      position: 'right',
      beginAtZero: true,
      title: {
        display: true,
        text: 'Avg Bookings per Student'
      },
      grid: {
        drawOnChartArea: false,
      }
    }
  }
}));

const totalUniqueStudents = computed(() => {
  return bookingData.value.bookings.reduce((sum, booking) => sum + (booking.uniqueStudents || 0), 0);
});

const averageBookingsPerStudent = computed(() => {
  const totalStudents = totalUniqueStudents.value;
  const totalBookingsCount = totalBookings.value;
  return totalStudents > 0 ? (totalBookingsCount / totalStudents).toFixed(2) : '0.00';
});

const loyaltyChartData = computed(() => {
  if (!loyaltyData.value.studentLoyalty.length) {
    return {labels: [], datasets: []};
  }

  const labels = loyaltyData.value.studentLoyalty.map(loyalty => loyalty.month);
  const firstTimeStudents = loyaltyData.value.studentLoyalty.map(loyalty => loyalty.firstTimeStudents);
  const returnStudents = loyaltyData.value.studentLoyalty.map(loyalty => loyalty.returnStudents);

  return {
    labels,
    datasets: [
      {
        label: 'First-Time Students',
        data: firstTimeStudents,
        backgroundColor: '#10b981',
        borderColor: '#059669',
        borderWidth: 1
      },
      {
        label: 'Return Students',
        data: returnStudents,
        backgroundColor: '#3b82f6',
        borderColor: '#1d4ed8',
        borderWidth: 1
      }
    ]
  };
});

const loyaltyChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    title: {
      display: false
    }
  },
  scales: {
    x: {
      stacked: true
    },
    y: {
      stacked: true,
      beginAtZero: true,
      title: {
        display: true,
        text: 'Number of Students'
      },
      ticks: {
        stepSize: 1
      }
    }
  }
}));

const totalFirstTimeStudents = computed(() => {
  return loyaltyData.value.studentLoyalty.reduce((sum, loyalty) => sum + (loyalty.firstTimeStudents || 0), 0);
});

const totalReturnStudents = computed(() => {
  return loyaltyData.value.studentLoyalty.reduce((sum, loyalty) => sum + (loyalty.returnStudents || 0), 0);
});

const teacherChartData = computed(() => {
  if (!teacherData.value.bookings.length) {
    return {labels: [], datasets: []};
  }

  const labels = teacherData.value.bookings.map(booking => booking.month);
  
  // Get all unique teachers across all months
  const allTeachers = new Map();
  teacherData.value.bookings.forEach(monthData => {
    if (monthData.teacherBookings) {
      Object.entries(monthData.teacherBookings).forEach(([teacherId, teacher]) => {
        allTeachers.set(teacherId, teacher.name);
      });
    }
  });

  // Generate colors for each teacher
  const colors = [
    '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
    '#f97316', '#06b6d4', '#84cc16', '#ec4899', '#6366f1'
  ];

  const bucketColors = {
    '15+ bookings': '#dc2626',      // red - high performers
    '10-14 bookings': '#f59e0b',    // orange - good performers  
    '5-9 bookings': '#10b981',      // green - moderate performers
    'Under 5 bookings': '#6b7280'  // gray - low performers
  };

  const datasets = Array.from(allTeachers.entries()).map(([teacherId, teacherName], index) => {
    const data = labels.map(month => {
      const monthData = teacherData.value.bookings.find(b => b.month === month);
      return monthData?.teacherBookings?.[teacherId]?.count || 0;
    });

    const bucketColor = bucketColors[teacherName] || colors[index % colors.length];
    const isLowPerformer = teacherName === 'Under 5 bookings';
    
    return {
      label: teacherName,
      data: data,
      backgroundColor: bucketColor,
      borderColor: bucketColor,
      borderWidth: 3,
      fill: false,
      pointRadius: 4,
      pointHoverRadius: 6,
      tension: 0.1,
      borderDash: isLowPerformer ? [5, 5] : []
    };
  });

  return {
    labels,
    datasets
  };
});

const teacherChartOptions = computed(() => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: true,
      position: 'top'
    },
    title: {
      display: false
    }
  },
  scales: {
    x: {
      stacked: false
    },
    y: {
      beginAtZero: true,
      title: {
        display: true,
        text: 'Number of Teachers'
      },
      ticks: {
        stepSize: 1
      }
    }
  }
}));

const totalTeachers = computed(() => {
  const allTeachers = new Set();
  teacherData.value.bookings.forEach(monthData => {
    if (monthData.teacherBookings) {
      Object.keys(monthData.teacherBookings).forEach(teacherId => {
        allTeachers.add(teacherId);
      });
    }
  });
  return allTeachers.size;
});

const totalTeacherBookings = computed(() => {
  return teacherData.value.total || 0;
});
</script>

<style scoped>
canvas {
  height: 300px !important;
}
</style>